import React, { useState, FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../assets/logo.svg";
import Button from "../../components/LoaderButton";
import URL_MAPPING from "../../routes/constants";
import ImageSection from "./imageSection";
import {
  resetPassLink,
  verifyMail,
  sendPasswordResetOtp,
  verifyResetOtp,
} from "../../api/login";
import LanguageSelector from "../../components/LangSelector";
import { useTranslation } from "react-i18next";
import OtpVerification from "./otpVerification";

const PASSWORD_RESET_OTP_TIMER_SECONDS = 10 * 60;

type SignUpScreen = "form" | "otp" | "sent";

const SignUp: React.FC = () => {
  const [username, setUsername] = useState<string>("");
  const [screen, setScreen] = useState<SignUpScreen>("form");
  const [error, setError] = useState<string>("");
  const [loader, setLoader] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState(PASSWORD_RESET_OTP_TIMER_SECONDS);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { isForgetPass, isResetPass } = location.state || {};
  const isPasswordRecovery = Boolean(isForgetPass || isResetPass);

  const errorTypeTranslation = (errorType: string) => {
    switch (errorType) {
      case "enter_email":
        return t("SignUp.enterEmailError");
      case "valid_email":
        return t("SignUp.validEmailError");
      case "user_exists":
        return t("SignUp.userExistsError");
      case "enter_verification_code":
        return t("SignUp.enterVerificationCode");
      case "invalid_response":
        return t("SignUp.invalidServerResponse");
      case "invalid_code":
        return t("SignUp.invalidCode");
      default:
        return errorType;
    }
  };

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!username) {
      return setError("enter_email");
    } else if (!emailRegex.test(username)) {
      return setError("valid_email");
    } else {
      setError("");
    }

    setLoader(true);
    try {
      const res: any = await verifyMail({ email: username });
      const body = res?.data;
      if (
        !isPasswordRecovery &&
        body?.mfa_enabled &&
        body?.password_enabled
      ) {
        setError("user_exists");
        return;
      }
      if (isPasswordRecovery) {
        await sendPasswordResetOtp({ email: username });
        setTimeLeft(PASSWORD_RESET_OTP_TIMER_SECONDS);
        setScreen("otp");
      } else {
        await resetPassLink({ email: username });
        setScreen("sent");
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail?.message || t("SignUp.errorMessage"));
    } finally {
      setLoader(false);
    }
  };

  const handleBackFromOtp = (): void => {
    setScreen("form");
    setError("");
  };

  const handleResendPasswordResetOtp = async (): Promise<void> => {
    setTimeLeft(PASSWORD_RESET_OTP_TIMER_SECONDS);
    setError("");
    try {
      await sendPasswordResetOtp({ email: username });
    } catch (err: any) {
      setError(err?.response?.data?.detail?.message || t("SignUp.errorMessage"));
    }
  };

  const handleVerifyPasswordResetOtp = async (otp: string[]): Promise<void> => {
    const pin = otp.join("");
    if (!pin) {
      return setError("enter_verification_code");
    }
    setLoader(true);
    setError("");
    try {
      const res: any = await verifyResetOtp({ email: username, otp: pin });
      const token =
        res?.access_token ??
        res?.token ??
        res?.data?.access_token ??
        res?.data?.token;
      if (!token) {
        setError("invalid_response");
        return;
      }
      navigate(
        `${URL_MAPPING.RESETPASS}?token=${encodeURIComponent(token)}`
      );
    } catch (err: any) {
      setError(err?.response?.data?.detail?.message || "invalid_code");
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <div className="flex h-screen flex-col md:flex-row">
        <div className="md:w-1/2 flex-col relative w-full h-[120px] md:h-full">
          <ImageSection />
        </div>

        {screen === "otp" && isPasswordRecovery ? (
          <OtpVerification
            handleSubmit={handleVerifyPasswordResetOtp}
            loader={loader}
            handleBack={handleBackFromOtp}
            error={error}
            handleGetOtpThroughMail={handleResendPasswordResetOtp}
            methodType="PASSWORD_RESET_OTP"
            timeLeft={timeLeft}
            setTimeLeft={setTimeLeft}
            username={username}
          />
        ) : (
          <div className="relative w-full md:w-1/2 flex flex-col justify-center items-center bg-white md:p-24 sm:p-14 p-10">
            <div className="mb-8">
              <img src={logo} alt="Logo" className="h-18" />
            </div>
            <div className="absolute top-[-12%] md:top-10 right-[13%]">
              <LanguageSelector />
            </div>
            {screen === "form" ? (
              <>
                <h2 className="text-2xl font-bold mb-6 text-purple">
                  {isForgetPass && t("SignUp.forgetP")}
                  {isResetPass && t("SignUp.resetP")}
                  {!isResetPass && !isForgetPass && t("SignUp.signUp")}
                </h2>
                <div className="mb-4 w-full">
                  <label className="block text-gray-700 text-13 font-semibold">
                    {t("SignUp.email")}
                  </label>
                  <input
                    type="text"
                    value={username}
                    placeholder={t("SignUp.enterEmail")}
                    onChange={(e) => setUsername(e.target.value)}
                    className="text-13 w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 bg-lightgrey h-12"
                  />
                </div>
                <div className="mb-4 w-full text-red-600 text-sm text-center mt-6">
                  {errorTypeTranslation(error)}
                </div>
                <Button
                  text={
                    isForgetPass || isResetPass
                      ? t("SignUp.submit")
                      : t("SignUp.signUp")
                  }
                  onClick={handleSubmit}
                  isLoading={loader}
                  disabled={loader}
                />
              </>
            ) : (
              <>
                <div className="text-purple text-l">
                  {t("SignUp.resetEmailSent")}
                </div>
              </>
            )}
            <div className="mt-8 text-13 text-grey flex flex-col md:flex-row gap-1 items-center">
              {isForgetPass ? t("SignUp.backTo") : t("SignUp.alreadyHaveAccount")}
              <span
                className="text-purple font-bold text-base cursor-pointer"
                onClick={() => navigate(URL_MAPPING.LOGIN)}
              >
                {t("SignUp.signIn")}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SignUp;
